<!-- Sysadmin-only: hand-made redirects of a group (spec 2026-10-07 §7).
     The functions sync writes them into the group .htaccess a few seconds
     after every change and reports a status per row. -->
<template>
  <div v-if="isSysAdmin" class="settings-panel custom-redirects-section mb-4">
    <h3 class="section-title">{{ $t('groupManagement.customRedirects.title') }}</h3>
    <p class="section-intro">{{ $t('groupManagement.customRedirects.intro') }}</p>

    <h4 class="block-title">
      {{ $t('groupManagement.customRedirects.ownTitle') }}
      <span class="count">{{ redirects.length }}</span>
    </h4>
    <b-table
      v-if="redirects.length"
      :items="redirects"
      :fields="fields"
      small
      responsive
      class="redirect-table mb-3"
    >
      <template #cell(from_path)="data"><code>{{ data.item.from_path }}</code></template>
      <template #cell(to_path)="data"><code>{{ data.item.to_path }}</code></template>
      <template #cell(permanent)="data">
        <b-form-select
          :value="data.item.permanent"
          :options="kindOptions"
          size="sm"
          @change="(value) => setPermanent(data.item, value)"
        />
      </template>
      <template #cell(status)="data">
        <span class="status" :class="`status-${statusVariant(data.item.status)}`">
          {{ statusLabel(data.item.status) }}
        </span>
      </template>
      <template #cell(actions)="data">
        <b-button size="sm" class="row-action row-action-danger" @click="remove(data.item)">
          {{ $t('groupManagement.customRedirects.delete') }}
        </b-button>
      </template>
    </b-table>
    <div ref="addRow" class="settings-subpanel add-row" :class="{ editing: editingAuto }">
      <b-row>
        <b-col sm="6">
          <b-form-group :label="$t('groupManagement.customRedirects.from')" label-for="custom-redirect-from">
            <b-form-input id="custom-redirect-from" v-model="newFrom" placeholder="/" />
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group :label="$t('groupManagement.customRedirects.to')" label-for="custom-redirect-to">
            <b-form-input id="custom-redirect-to" v-model="newTo" placeholder="/ausgaben/heft-12" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="align-items-end">
        <b-col sm="6">
          <b-form-group :label="$t('groupManagement.customRedirects.kind')" label-for="custom-redirect-kind">
            <b-form-select id="custom-redirect-kind" v-model="newPermanent" :options="kindOptions" />
          </b-form-group>
        </b-col>
        <b-col sm="6" class="text-right">
          <b-button class="green-button add-button" :disabled="!canAdd" @click="add">
            {{ $t('groupManagement.customRedirects.add') }}
          </b-button>
        </b-col>
      </b-row>
      <div v-if="inputError" class="input-error">{{ inputError }}</div>
      <b-form-text v-if="editingAuto">{{ $t('groupManagement.customRedirects.editAutoHint') }}</b-form-text>
      <b-form-text>{{ $t('groupManagement.customRedirects.delayHint') }}</b-form-text>
    </div>

    <h4 class="block-title mt-5">
      {{ $t('groupManagement.customRedirects.autoTitle') }}
      <span class="count">{{ autoRedirects.length }}</span>
    </h4>
    <p class="block-intro">{{ $t('groupManagement.customRedirects.autoIntro') }}</p>
    <b-table
      v-if="autoRedirects.length"
      :items="autoRedirects"
      :fields="autoFields"
      small
      responsive
      class="redirect-table auto-redirects mb-0"
    >
      <template #cell(from_path)="data"><code>{{ data.item.from_path }}</code></template>
      <template #cell(to_path)="data"><code>{{ data.item.to_path }}</code></template>
      <template #cell(type)="data">
        <span class="scope">{{ $t(`groupManagement.customRedirects.autoType.${data.item.type}`) }}</span>
      </template>
      <template #cell(actions)="data">
        <b-button size="sm" class="row-action" @click="editAuto(data.item)">
          {{ $t('groupManagement.customRedirects.edit') }}
        </b-button>
        <b-button size="sm" class="row-action row-action-danger" @click="removeAuto(data.item)">
          {{ $t('groupManagement.customRedirects.delete') }}
        </b-button>
      </template>
    </b-table>
    <b-form-text v-else>{{ $t('groupManagement.customRedirects.autoEmpty') }}</b-form-text>
  </div>
</template>

<script>
import GetCustomRedirects from '@/graphQlQueries/queries/getCustomRedirects';
import InsertCustomRedirect from '@/graphQlQueries/mutations/insertCustomRedirect';
import UpdateCustomRedirect from '@/graphQlQueries/mutations/updateCustomRedirect';
import DeleteCustomRedirect from '@/graphQlQueries/mutations/deleteCustomRedirect';
import GetAutoRedirects from '@/graphQlQueries/queries/getAutoRedirects';
import DeleteAutoRedirect from '@/graphQlQueries/mutations/deleteAutoRedirect';
import { normalizeRedirectPath } from '@/utils/redirectPath';

// The sync waits 5 s after the last change, then needs a moment to write.
const STATUS_REFRESH_MS = 7000;

export default {
  name: 'CustomRedirectsSection',
  props: {
    groupId: { type: Number, required: true },
    isSysAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      redirects: [],
      autoRedirects: [],
      editingAuto: false,
      newFrom: '',
      newTo: '',
      newPermanent: false,
      inputError: null,
      refreshTimer: null,
    };
  },
  computed: {
    fields() {
      return [
        { key: 'from_path', label: this.$t('groupManagement.customRedirects.from') },
        { key: 'to_path', label: this.$t('groupManagement.customRedirects.to') },
        { key: 'permanent', label: this.$t('groupManagement.customRedirects.kind') },
        { key: 'status', label: this.$t('groupManagement.customRedirects.status') },
        { key: 'actions', label: '', tdClass: 'text-nowrap text-right' },
      ];
    },
    autoFields() {
      return [
        { key: 'from_path', label: this.$t('groupManagement.customRedirects.from') },
        { key: 'to_path', label: this.$t('groupManagement.customRedirects.to') },
        { key: 'type', label: this.$t('groupManagement.customRedirects.autoScope') },
        { key: 'actions', label: '', tdClass: 'text-nowrap text-right' },
      ];
    },
    kindOptions() {
      return [
        { value: false, text: this.$t('groupManagement.customRedirects.temporary') },
        { value: true, text: this.$t('groupManagement.customRedirects.permanent') },
      ];
    },
    canAdd() {
      return this.newFrom.trim() !== '' && this.newTo.trim() !== '';
    },
  },
  watch: {
    groupId: 'load',
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer);
  },
  methods: {
    async load() {
      if (!this.isSysAdmin) return;
      const result = await this.$apollo.query({
        query: GetCustomRedirects,
        variables: { groupId: this.groupId },
        fetchPolicy: 'network-only',
      });
      this.redirects = result.data.custom_redirects || [];
      const auto = await this.$apollo.query({
        query: GetAutoRedirects,
        variables: { groupId: this.groupId },
        fetchPolicy: 'network-only',
      });
      this.autoRedirects = auto.data.autoRedirects || [];
    },
    // One reload once the sync has had time to write the status.
    refreshLater() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.load(), STATUS_REFRESH_MS);
    },
    async add() {
      const fromPath = normalizeRedirectPath(this.newFrom, { allowRoot: true });
      const toPath = normalizeRedirectPath(this.newTo, { allowRoot: false });
      if (!fromPath || !toPath) {
        this.inputError = this.$t('groupManagement.customRedirects.invalidPath');
        return;
      }
      if (this.redirects.some((r) => r.from_path === fromPath)) {
        this.inputError = this.$t('groupManagement.customRedirects.duplicate');
        return;
      }
      this.inputError = null;
      await this.$apollo.mutate({
        mutation: InsertCustomRedirect,
        variables: {
          groupId: this.groupId, fromPath, toPath, permanent: this.newPermanent,
        },
      });
      this.newFrom = '';
      this.newTo = '';
      this.newPermanent = false;
      this.editingAuto = false;
      await this.load();
      this.refreshLater();
    },
    async setPermanent(item, permanent) {
      await this.$apollo.mutate({ mutation: UpdateCustomRedirect, variables: { id: item.id, permanent } });
      await this.load();
      this.refreshLater();
    },
    async remove(item) {
      if (!(await this.confirmDelete(item))) return;
      await this.$apollo.mutate({ mutation: DeleteCustomRedirect, variables: { id: item.id } });
      await this.load();
      this.refreshLater();
    },
    // Editing an automatic redirect = a custom one on exactly that path; it
    // goes first, deeper paths keep following the automatic rule.
    editAuto(item) {
      this.newFrom = item.from_path;
      this.newTo = item.to_path;
      this.newPermanent = true;
      this.inputError = null;
      this.editingAuto = true;
      this.$refs.addRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    async removeAuto(item) {
      if (!(await this.confirmDelete(item))) return;
      await this.$apollo.mutate({
        mutation: DeleteAutoRedirect,
        variables: { groupId: this.groupId, fromPath: item.from_path },
      });
      await this.load();
      this.refreshLater();
    },
    confirmDelete(item) {
      return this.$bvModal.msgBoxConfirm(
        this.$t('groupManagement.customRedirects.deleteConfirm', { from: item.from_path, to: item.to_path }),
        {
          okTitle: this.$t('groupManagement.customRedirects.delete'),
          okVariant: 'danger',
          cancelTitle: this.$t('buttons.cancel'),
          centered: true,
        },
      ).catch(() => false);
    },
    statusLabel(status) {
      const key = {
        aktiv: 'statusActive', 'ziel-fehlt': 'statusMissingTarget', schleife: 'statusLoop',
      }[status] || 'statusPending';
      return this.$t(`groupManagement.customRedirects.${key}`);
    },
    statusVariant(status) {
      return { aktiv: 'success', 'ziel-fehlt': 'warning', schleife: 'danger' }[status] || 'secondary';
    },
  },
};
</script>

<style scoped lang="scss">
@import './settingsPanel';

.add-row.editing {
  border-color: $webmag-green;
}

.add-button {
  min-width: 140px;
  margin-bottom: 1rem;
}

.input-error {
  color: #f08080;
  font-size: 13px;
  margin-bottom: 4px;
}

.redirect-table {
  ::v-deep th {
    color: $settings-form-text;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    border-top: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }

  ::v-deep th,
  ::v-deep td {
    padding-right: 16px;
  }

  ::v-deep td {
    color: #e6e6e6;
    vertical-align: middle;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  ::v-deep tbody tr:hover td {
    background-color: rgba(255, 255, 255, 0.03);
  }

  code {
    color: #fff;
    background: none;
    font-size: 13px;
    white-space: nowrap;
  }
}

.scope {
  color: $settings-form-text;
  font-size: 13px;
}

.status {
  font-size: 12px;
  font-weight: 700;

  &.status-success { color: $webmag-green; }
  &.status-warning { color: #f0ad4e; }
  &.status-danger { color: #f08080; }
  &.status-secondary { color: $settings-form-text; }
}

.row-action {
  color: $webmag-light-grey;
  background: transparent;
  border: none;
  padding: 2px 8px;

  &:hover,
  &:focus {
    color: #fff;
    background: transparent;
    box-shadow: none;
  }
}

.row-action-danger:hover,
.row-action-danger:focus {
  color: #f08080;
}
</style>
